import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { displayError } from '../../helpers';
import { getSiteDocuments } from '../../services/CustomerSiteServices';
import { fetchPDF } from '../../services/QuotationServices';
import { printSalesOrder } from '../../services/SalesOrderServices';
import { printWorkScheduleReport } from '../../services/WorkScheduleServices';
import { printDesignMix } from '../../services/DesignMixServices';
import { printDeliveryChallan } from '../../services/DeliveryChallanServices';
import { printSalesInvoice } from '../../services/SalesInvoiceServices';
import { openPdfResponse } from '../../utils/openPdfInApp';

export const SITE_DOC_LABELS = {
  quotation: 'Quotation',
  sales_order: 'Sales Order',
  schedule: 'Schedules',
  design_mix: 'Design Mix',
  delivery_challan: 'Delivery Challan',
  invoice: 'Invoice',
};

function formatDocDate(value) {
  if (!value) return '-';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD-MM-YYYY') : String(value);
}

async function downloadSiteDocument(cookies, docType, docId, label) {
  let response;
  let title = SITE_DOC_LABELS[docType] || 'Document';
  let fileName = `${docType}_${docId}.pdf`;

  switch (docType) {
    case 'quotation':
      response = await fetchPDF(cookies, docId);
      title = `Quotation ${label || docId}`;
      fileName = `quotation_${label || docId}.pdf`;
      break;
    case 'sales_order':
      response = await printSalesOrder(cookies, docId);
      title = `Sales Order ${label || docId}`;
      fileName = `sales_order_${label || docId}.pdf`;
      break;
    case 'schedule':
      response = await printWorkScheduleReport(cookies, docId);
      title = `Schedule ${label || docId}`;
      fileName = `schedule_${label || docId}.pdf`;
      break;
    case 'design_mix':
      response = await printDesignMix(cookies, docId);
      title = `Design Mix ${label || docId}`;
      fileName = `design_mix_${label || docId}.pdf`;
      break;
    case 'delivery_challan':
      response = await printDeliveryChallan(cookies, docId);
      title = `Delivery Challan ${label || docId}`;
      fileName = `delivery_challan_${label || docId}.pdf`;
      break;
    case 'invoice':
      response = await printSalesInvoice(cookies, docId);
      title = `Invoice ${label || docId}`;
      fileName = `invoice_${label || docId}.pdf`;
      break;
    default:
      throw new Error('Unsupported document type');
  }

  openPdfResponse(response, {
    title,
    downloadFileName: fileName.replace(/[^\w.\-]+/g, '_'),
  });
}

function SiteDocumentsModal({
  open,
  onClose,
  cookies,
  siteId,
  siteName,
  docType,
}) {
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [documents, setDocuments] = useState([]);

  const loadDocuments = useCallback(() => {
    if (!open || !siteId || !docType) return;
    setLoading(true);
    setDocuments([]);
    getSiteDocuments(cookies, siteId, docType)
      .then((response) => {
        setDocuments(response.documents || []);
      })
      .catch((error) => {
        displayError(error?.response?.data ?? error.message, 'Failed to load documents');
        setDocuments([]);
      })
      .finally(() => setLoading(false));
  }, [cookies, docType, open, siteId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  if (!open) return null;

  const title = `${SITE_DOC_LABELS[docType] || 'Documents'} — ${siteName || 'Site'}`;

  const onDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      await downloadSiteDocument(cookies, docType, doc.id, doc.doc_no || doc.label);
    } catch (error) {
      displayError(error?.response?.data ?? error.message, 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="site-docs-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="site-docs-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="site-docs-modal-header">
          <h5>{title}</h5>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="site-docs-modal-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-4 text-muted">No documents found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered site-docs-modal-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Document No</th>
                    <th>Date</th>
                    <th>Details</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr key={doc.id}>
                      <td>{index + 1}</td>
                      <td>{doc.doc_no || doc.label || doc.id}</td>
                      <td>{formatDocDate(doc.doc_date)}</td>
                      <td>{doc.extra || '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={downloadingId === doc.id}
                          onClick={() => onDownload(doc)}
                        >
                          {downloadingId === doc.id ? 'Opening…' : 'Download'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function formatSiteAddress(site = {}) {
  return [site.address_1, site.address_2, site.address_3].filter(Boolean).join(', ');
}

export function formatSiteContact(site = {}) {
  return site.contact_mobile_no || site.mobile_number || site.phone_number || '';
}

export function SiteDocCountCell({ site, docType, onOpen }) {
  const count = site?.document_counts?.[docType];
  const siteId = site?.id;
  if (!siteId) {
    return <span className="text-muted">-</span>;
  }
  const value = Number(count || 0);
  if (value <= 0) {
    return <span className="text-muted">0</span>;
  }
  return (
    <button
      type="button"
      className="site-doc-count-btn"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen(site, docType);
      }}
      title={`View ${SITE_DOC_LABELS[docType]}`}
    >
      {value}
    </button>
  );
}

export default SiteDocumentsModal;
